import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Boxes,
  CircleCheck,
  CircleX,
  Filter as FilterIcon,
  MoreVertical,
  Wallet,
  UserRoundCog,
} from "lucide-react";

import { Filter } from "../../../ui/Filter";
import { CAMPO } from "../../../ui/Filter/sizes";
import { Input } from "../../../ui/Form/Input";
import { Select } from "../../../ui/Form/Select";
import { Summary } from "../../../ui/Summary";
import { Table } from "../../../ui/Table";
import { Pagination } from "../../../ui/Pagination";
import { formatMoney } from "../../../../services/functions";

dayjs.extend(customParseFormat);

const API_FORMAT = "YYYY-MM-DD";
const VIEW_FORMAT = "DD/MM/YYYY";

const SORTABLE_COLUMN_KEYS = {
  Cliente: "customerName",
  Pet: "pet",
  Pacote: "package",
  Vendedor: "seller",
  "Preço c/ desconto": "totalPrice",
  Status: "status",
  Data: "date",
};

/** O campo mostra DD/MM/AAAA; a API espera AAAA-MM-DD. */
function paraApi(valor) {
  const data = dayjs(valor, VIEW_FORMAT, true);
  return data.isValid() ? data.format(API_FORMAT) : "";
}

function paraTela(valor) {
  if (!valor) return "";
  const data = dayjs(valor);
  return data.isValid() ? data.format(VIEW_FORMAT) : "";
}

/**
 * Aba "Pacotes vendidos".
 *
 * Antes era uma tabela do Material UI com apenas duas datas de filtro e nenhum
 * total. Os filtros de status, CPF e busca, a ordenacao e os quatro KPIs sao
 * resolvidos no servidor (packages/orders), entao os numeros valem para o
 * conjunto filtrado inteiro e nao para a pagina carregada.
 */
export function SoldPackages({
  packageSold,
  pagination,
  panel,
  primeiroDia,
  dataFinal,
  onFilter,
  onPageChange,
  podeIntegrar,
  onSendToErp,
  onDelete,
  onOpenCustomer,
}) {
  const [filtro, setFiltro] = useState({
    dateFrom: primeiroDia,
    dateTo: dataFinal,
    status: "*",
    cpf: "",
    search: "",
  });
  const [sort, setSort] = useState({ sortBy: null, sortOrder: "desc" });
  const [menuAberto, setMenuAberto] = useState(null);

  const carregando = !packageSold;

  const altera = (campos) => setFiltro((atual) => ({ ...atual, ...campos }));

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    onFilter(1, { ...filtro, ...sort });
  };

  const handleClear = () => {
    const limpo = {
      dateFrom: primeiroDia,
      dateTo: dataFinal,
      status: "*",
      cpf: "",
      search: "",
    };
    setFiltro(limpo);
    setSort({ sortBy: null, sortOrder: "desc" });
    onFilter(1, limpo);
  };

  const handleSortColumn = (sortBy) => {
    const sortOrder =
      sort.sortBy === sortBy && sort.sortOrder === "asc" ? "desc" : "asc";
    const novo = { sortBy, sortOrder };

    setSort(novo);
    onFilter(1, { ...filtro, ...novo });
  };

  const totais = useMemo(
    () => ({
      vendidos: panel?.totalSold ?? 0,
      integrados: panel?.totalIntegrated ?? 0,
      cancelados: panel?.totalCanceled ?? 0,
      valor: panel?.totalValue ?? 0,
    }),
    [panel]
  );

  return (
    <div className="flex w-full flex-col gap-5 pt-5">
      <Filter.Root onSubmit={handleSubmit}>
        <Filter.Container>
          <Input.Root
            pickDate
            label="Início"
            value={paraTela(filtro.dateFrom)}
            onChange={(valor) => altera({ dateFrom: paraApi(valor) })}
          />

          <Input.Root
            pickDate
            label="Fim"
            value={paraTela(filtro.dateTo)}
            onChange={(valor) => altera({ dateTo: paraApi(valor) })}
          />

          <Select.Root
            label="Status"
            style={CAMPO.medio}
            value={filtro.status}
            onChange={(status) => altera({ status })}
          >
            <Select.Option value="*">Todos</Select.Option>
            <Select.Option value="CRIADO">Criado</Select.Option>
            <Select.Option value="INTEGRADO">Integrado</Select.Option>
            <Select.Option value="UTILIZADO">Utilizado</Select.Option>
            <Select.Option value="CANCELADO">Cancelado</Select.Option>
          </Select.Root>

          <Input.Root
            label="CPF"
            mask="999.999.999-99"
            style={CAMPO.medio}
            value={filtro.cpf}
            onChange={(cpf) => altera({ cpf })}
          />

          <Input.Root
            label="Cliente ou pet"
            placeholder="Nome completo ou parte dele"
            style={CAMPO.busca}
            value={filtro.search}
            onChange={(search) => altera({ search })}
          />
        </Filter.Container>

        <Filter.Actions>
          <Filter.Action isLoading={carregando}>
            <Filter.ActionIcon icon={FilterIcon} isLoading={carregando} />
            Filtrar
          </Filter.Action>

          <Filter.Action
            outline
            type="button"
            name="clear-sold-packages"
            onClick={handleClear}
          >
            Limpar
          </Filter.Action>
        </Filter.Actions>
      </Filter.Root>

      <Summary.Root>
        <Summary.Box>
          <Summary.Icon icon={Boxes} />
          <Summary.TextContent>
            <Summary.Title size="big">{totais.vendidos}</Summary.Title>
            <Summary.Text>Pacotes vendidos</Summary.Text>
          </Summary.TextContent>
        </Summary.Box>

        <Summary.Box>
          <Summary.Icon icon={CircleCheck} />
          <Summary.TextContent>
            <Summary.Title size="big">{totais.integrados}</Summary.Title>
            <Summary.Text>Integrados</Summary.Text>
          </Summary.TextContent>
        </Summary.Box>

        <Summary.Box>
          <Summary.Icon icon={CircleX} />
          <Summary.TextContent>
            <Summary.Title size="big">{totais.cancelados}</Summary.Title>
            <Summary.Text>Cancelados</Summary.Text>
          </Summary.TextContent>
        </Summary.Box>

        <Summary.Box>
          <Summary.Icon icon={Wallet} />
          <Summary.TextContent>
            <Summary.Title size="big">R$ {formatMoney(totais.valor)}</Summary.Title>
            <Summary.Text>Valor total</Summary.Text>
          </Summary.TextContent>
        </Summary.Box>
      </Summary.Root>

      <Table.Root name="table-sold-packages" config>
        <Table.Header
          columns={[
            "Cliente:minmax(150px, 260px)",
            "Pet:minmax(105px, 200px)",
            "Pacote:minmax(150px, 260px)",
            "Vendedor:minmax(120px, 200px)",
            "Preço c/ desconto:130px",
            "Status:110px",
            "Data:100px",
            ...(podeIntegrar ? ["Ações:70px"] : []),
          ]}
          sortableConfig={{
            sortBy: sort.sortBy,
            sortOrder: sort.sortOrder,
            onSort: handleSortColumn,
            columnKeys: SORTABLE_COLUMN_KEYS,
          }}
        />

        <Table.Content isLoading={carregando}>
          {(packageSold || []).map((row) => (
            <Table.Line key={row.packageOrderId}>
              <Table.Item title={row.customerName}>
                <button
                  type="button"
                  className="flex items-center gap-1 border-b border-transparent font-medium hover:border-current"
                  onClick={() => onOpenCustomer(row.customerId)}
                >
                  <UserRoundCog aria-hidden="true" size={16} />
                  {row.customerName}
                </button>
              </Table.Item>
              <Table.Item>{row.pet}</Table.Item>
              <Table.Item>{row.package}</Table.Item>
              <Table.Item>{row.seller || "-"}</Table.Item>
              <Table.Item className="text-right">
                R$ {formatMoney(row.totalPrice)}
              </Table.Item>
              <Table.Item>{row.status}</Table.Item>
              <Table.Item>{row.date}</Table.Item>

              {podeIntegrar && (
                <Table.Item>
                  <div className="relative">
                    <button
                      type="button"
                      title="Ações"
                      className="text-gray-700 hover:text-scale-600"
                      onClick={() =>
                        setMenuAberto(
                          menuAberto === row.packageOrderId ? null : row.packageOrderId
                        )
                      }
                    >
                      <MoreVertical size={18} />
                    </button>

                    {menuAberto === row.packageOrderId && (
                      <div className="absolute right-0 top-6 z-20 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                        <button
                          type="button"
                          disabled={row.status === "INTEGRADO"}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={() => {
                            setMenuAberto(null);
                            onSendToErp(row.packageOrderId);
                          }}
                        >
                          Enviar para o ERP
                        </button>
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100"
                          onClick={() => {
                            setMenuAberto(null);
                            onDelete(row.packageOrderId);
                          }}
                        >
                          Deletar pacote
                        </button>
                      </div>
                    )}
                  </div>
                </Table.Item>
              )}
            </Table.Line>
          ))}
        </Table.Content>

        {!!pagination && (
          <Pagination.Root
            name="pagination-sold-packages"
            totalPages={pagination.total_pages}
            currentPage={pagination.page - 1}
            onChange={(pagina) => onPageChange(pagina + 1, { ...filtro, ...sort })}
          >
            <Pagination.Back />
            <Pagination.Pages />
            <Pagination.Forward />
          </Pagination.Root>
        )}
      </Table.Root>
    </div>
  );
}
